import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Target as TargetIcon, Plus } from "lucide-react";

export default function Targets() {
  const { user } = useAuth();
  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", period: new Date().toISOString().slice(0,7), target_value: 0, incentive_rate_pct: 2.5 });

  const load = async () => { const { data } = await api.get("/sales/targets"); setItems(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isManager) api.get("/sales/team").then(({ data }) => setTeam(data)); /* eslint-disable-next-line */ }, [isManager]);

  const save = async () => { try { await api.post("/sales/targets", form); toast.success("Target set"); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-targets">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Targets & incentives</h1>
          <p className="text-muted-foreground">Monthly/quarterly targets per executive with live achievement % and incentive estimates.</p>
        </div>
        {isManager && <Button onClick={() => setOpen(true)} data-testid="target-set-btn"><Plus className="w-4 h-4 mr-1" /> Set target</Button>}
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>User</TableHead><TableHead>Target</TableHead><TableHead>Achieved</TableHead><TableHead>%</TableHead><TableHead>Incentive est.</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((t) => {
              const userInfo = team.find((u) => u.id === t.user_id);
              return (
                <TableRow key={t.id} data-testid={`target-row-${t.id}`}>
                  <TableCell className="font-mono text-xs">{t.period}</TableCell>
                  <TableCell>{userInfo?.full_name || userInfo?.email || t.user_id.slice(0, 8)}</TableCell>
                  <TableCell>₹{(t.target_value || 0).toLocaleString()}</TableCell>
                  <TableCell>₹{(t.achieved || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="outline">{t.achievement_pct || 0}%</Badge></TableCell>
                  <TableCell>₹{((t.achieved || 0) * (t.incentive_rate_pct || 0) / 100).toFixed(0)}</TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10"><TargetIcon className="w-8 h-8 inline-block mb-2 text-muted-foreground" /><div>No targets yet.</div></TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set target</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Executive</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="target-user"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name || t.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Period (YYYY-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="target-period" /></div>
            <div><Label>Target value (₹)</Label><Input type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: parseFloat(e.target.value || "0") })} data-testid="target-value" /></div>
            <div><Label>Incentive rate %</Label><Input type="number" value={form.incentive_rate_pct} onChange={(e) => setForm({ ...form, incentive_rate_pct: parseFloat(e.target.value || "0") })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="target-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
